import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { iniciarSesion } from '../nucleo/api/nucleoApi.js';
import { obtenerFechaDeNegocioHoy } from '../nucleo/fecha/fechaLocal.js';
import { useSesion } from '../nucleo/sesion/ProveedorDeSesion.jsx';
import { resolverDestinoPosteriorAlAcceso } from '../nucleo/sesion/destinoPosteriorAlAcceso.js';
import AvisoDeError from '../nucleo/avisos/AvisoDeError.jsx';

/**
 * Pantalla de acceso (RF-004).
 *
 * Sin sesión no se muestra ningún dato de negocio y NO hay barra de navegación:
 * el acceso permanente a Ajustes existe solo con sesión iniciada (RN-044f).
 * El tema, en cambio, sí aplica aquí: es del dispositivo y no depende de la
 * sesión (RN-045d).
 *
 * LO QUE EL FIX DE INGRESOS CAMBIÓ AQUÍ (punto 2, RF-FIX-L01): los 11 desvíos
 * DV-01 a DV-11 que esta pantalla tenía frente al prototipo de referencia
 * `diseno/prototipo-v35-acceso.html`, listados uno por uno en
 * `analisis/requerimientos-ingresos-fix-login.md` rev. 3. Cada corrección queda
 * marcada abajo con su DV. Lo que NO cambia: el destino posterior al acceso, las
 * validaciones, la duración de la sesión y el componente de aviso de error del
 * núcleo (RF-139, RN-FIX-L02).
 */

/*
 * DV-03 · EL ENCABEZADO ES UNA FRANJA DE COLOR PRIMARIO A TODO LO ANCHO, no un
 * título plano centrado. Son las medidas del bloque `.logo` del v35 (línea 24 del
 * prototipo). Van como estilo de esta pantalla y NO en `estilos/base.css`: la
 * regla global de rótulos la comparten las 21 pantallas y DV-06 pide la caja alta
 * SOLO en el formulario de acceso.
 *
 * Los márgenes negativos cancelan el relleno de `.centrado` (16px arriba, 12px a
 * los lados) para que la franja llegue a los dos bordes del marco, como la dibuja
 * el prototipo.
 */
const ESTILO_FRANJA = {
  background: 'var(--pri)',
  color: 'var(--priTx)',
  padding: '26px 16px 22px',
  textAlign: 'center',
  margin: '-16px -12px 0',
};

const ESTILO_NOMBRE = { display: 'block', fontSize: 20, lineHeight: 1.2 };

const ESTILO_LEMA = { display: 'block', fontSize: 12.2, opacity: 0.9, marginTop: 4 };

/*
 * DV-06 · LOS RÓTULOS DEL FORMULARIO DE ACCESO, EN CAJA ALTA. Es la presentación
 * de `label` del v35 (línea 29): chicos, con espaciado entre letras, en color
 * secundario y en mayúsculas por estilo. El texto escrito sigue siendo «Clave» y
 * «Contraseña» (DV-05): la caja alta la pone la presentación, no el literal, que
 * es lo que lee un lector de pantalla.
 */
const ESTILO_ROTULO = {
  fontSize: 11.4,
  letterSpacing: '.4px',
  textTransform: 'uppercase',
  color: 'var(--sec)',
  fontWeight: 800,
  margin: '0 0 4px',
};

/* DV-10 · el título en negrita del recuadro informativo, como primera línea. */
const ESTILO_TITULO_DEL_AVISO = { display: 'block', marginBottom: 3 };

export default function Acceso() {
  const navegar = useNavigate();
  const ubicacion = useLocation();
  const { establecerSesion } = useSesion();

  const [clave, setClave] = useState('');
  const [contrasena, setContrasena] = useState('');
  const [error, setError] = useState(ubicacion.state?.aviso ?? null);
  const [enviando, setEnviando] = useState(false);

  async function entrar(evento) {
    evento.preventDefault();
    setError(null);

    /*
     * H-03 del análisis · el mensaje nombra el campo con el rótulo que el campo
     * tiene. Decía «Escribe tu clave de acceso.» cuando el rótulo decía «Clave de
     * acceso»; con DV-05 el rótulo es «Clave» y el mensaje lo sigue. Es la misma
     * regla que ya aplica el traspaso (D-261): un aviso que nombra el campo con
     * otras palabras obliga a la persona a traducir para encontrarlo.
     */
    if (!clave.trim()) { setError({ mensaje: 'Escribe tu clave.' }); return; }
    if (!contrasena) { setError({ mensaje: 'Escribe tu contraseña.' }); return; }

    setEnviando(true);
    try {
      const sesion = await iniciarSesion(clave.trim(), contrasena, obtenerFechaDeNegocioHoy());
      establecerSesion(sesion);
      // El destino NO se decide aquí: lo resuelve la regla única RN-621 (D-319),
      // que conserva la precedencia del cambio obligatorio de contraseña del
      // 13/16 (RN-032) y, en cualquier otro caso, aterriza en el Tablero (RF-219).
      navegar(resolverDestinoPosteriorAlAcceso(sesion), { replace: true });
    } catch (fallo) {
      setError(fallo);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="centrado">
      {/* DV-01 (D-480) · el nombre queda en «Gastos Personales»: es el literal que
          fijó Deisy Paez y el que la aplicación ya mostraba. El v35 se contradecía
          solo en este punto (la franja dibuja «Gastos del hogar» y su propio
          `<title>` dice «Gastos Personales»), y D-480 lo zanja.
          DV-02 · el segundo renglón pasa a ser el del prototipo; decía «Control de
          gastos del hogar», que repetía el nombre en vez de decir qué hacer aquí. */}
      <div style={ESTILO_FRANJA} data-prueba="acceso-encabezado">
        <b style={ESTILO_NOMBRE}>Gastos Personales</b>
        <small style={ESTILO_LEMA}>Entra con tu clave y tu contraseña</small>
      </div>

      <form className="card" onSubmit={entrar}>
        {/* DV-04 · la tarjeta NO lleva título: el v35 arranca con el error oculto y
            el rótulo de la clave. El «Iniciar sesión» que había aquí nombraba otra
            vez lo que el botón ya dice.
            DV-07 (RN-FIX-L02) · el aviso de error va DENTRO de la tarjeta, arriba
            del rótulo de la clave, y sigue siendo la pieza única del núcleo
            (RF-139): no se dibuja ninguna franja nueva. */}
        <AvisoDeError error={error} margen="0 0 11px" />

        {/* DV-05 · el rótulo es «Clave», no «Clave de acceso». La caja alta es
            presentación (DV-06, ESTILO_ROTULO). */}
        <label htmlFor="clave" style={ESTILO_ROTULO}>Clave</label>
        <input id="clave" value={clave} onChange={(e) => setClave(e.target.value)} placeholder="Tu clave" autoComplete="username" />

        <label htmlFor="contrasena" style={ESTILO_ROTULO}>Contraseña</label>
        <input id="contrasena" type="password" value={contrasena} onChange={(e) => setContrasena(e.target.value)} placeholder="Tu contraseña" autoComplete="current-password" />

        <button className="btn ancho" type="submit" disabled={enviando}>
          {enviando ? 'Entrando…' : 'Entrar'}
        </button>

        {/* Fila 33 del inventario (RN-627a, D-333): cae el código «(S-12)». El destino
            se verificó contra la aplicación: existe como renglón de Ajustes (Ajustes.jsx)
            y como pantalla propia en /ajustes/usuarios.

            [RENOMBRADO 2026-10-10 · incremento 16/17 · RN-647, D-347] El destino pasa a
            llamarse «Personas del hogar».

            [DV-09 · FIX ingresos, D-481] La frase final pasa a «No se recupera por
            correo.», que es el literal del v35. El literal de RN-626 («No hay
            recuperación por correo») queda SUPERSEDED ÚNICAMENTE EN ESTA PANTALLA:
            sigue vigente donde aplicara en el resto de la aplicación. El dato que
            RN-626 protege no se pierde —se sigue diciendo que por correo no se
            recupera—; lo único que cambia son las palabras. */}
        <div className="ayuda" style={{ marginTop: 12 }} data-prueba="acceso-contrasena-olvidada">
          ¿Olvidaste tu contraseña? La restablece la otra persona del hogar desde
          <b> Ajustes › Personas del hogar</b>. No se recupera por correo.
        </div>
      </form>

      {/* Fila 37 del inventario (rev. 6, D-337, CA-15-14): «dato de negocio» es
          vocabulario de implementación y no vuelve (RN-627e).

          [DV-10 y DV-11 · FIX ingresos, D-481] El recuadro gana el título en negrita
          del v35 y su cuerpo pasa al literal del prototipo. El literal de D-337 /
          RN-629 queda SUPERSEDED ÚNICAMENTE EN ESTA PANTALLA. Los dos datos útiles
          se conservan: que sin entrar no se ve nada y cada cuánto hay que volver a
          entrar (treinta días desde el último uso). */}
      <div className="aviso i" data-prueba="acceso-aviso-sesion">
        <b style={ESTILO_TITULO_DEL_AVISO}>Mientras no entres no se ve nada tuyo</b>
        Sin haber entrado no se muestra ningún movimiento, saldo ni cuenta. Una vez
        dentro, la sesión se mantiene treinta días desde la última vez que usaste la
        aplicación.
      </div>
    </div>
  );
}
